<template>
  <span>
    <authorize-integration-modal
      :value="showAuthorizationModal"
      :integration="authorizeIntegrationCandidate"
      :authorized="authorized"
      @authorize="handleAuthorize"
      @cancel="handleCancel"
      @close="handleCancel"
    />
    <v-container
      id="manage-integrations"
      tag="section"
    >
      <div class="mx-auto section-container">
        <div class="section-header">
          <span class="section-header--text">AutoResolve</span>
          <span>
            <v-icon
              size="18"
              color="white"
            >
              $faInfoCircle
            </v-icon>
            <a
              href="https://help.noiseaware.io/hc/en-us/articles/4406268412947-AutoResolve"
              class="section-hint"
              target="_blank"
            >What is AutoResolve?</a>
          </span>
        </div>
        <div
          v-if="selectedOrg.id"
          class="section-body"
        >
          <div>
            <h2 class="integration-list--headers">
              Active Integrations
            </h2>
            <hr class="integration-list--rule">
            <div v-if="activeIntegrationsList.length > 0">
              <template v-for="(integration, id) in activeIntegrationsList">
                <integration-item
                  :key="id"
                  v-bind="integration"
                  @deactivate-integration="beginDeactivationSequence"
                />
              </template>
            </div>
            <div
              v-else
              class="active-integration--empty"
            >
              <v-icon class="active-integration--empty-icon">
                $faLink
              </v-icon>
              <h2 class="active-integration--empty-header">
                Connect to an available integration to enable AutoResolve
              </h2>
            </div>
          </div>
          <div v-if="availableIntegrationsList.length > 0">
            <h2 class="integration-list--headers">
              Available Integrations
            </h2>
            <hr class="integration-list--rule">
            <template v-for="(integration, id) in availableIntegrationsList">
              <integration-item
                v-if="integration"
                :key="id"
                v-bind="integration"
                @enable-integration="integration.enableIntegration(integration)"
              />
            </template>
          </div>
        </div>
      </div>
    </v-container>
  </span>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { CONNECT, WAITING_FOR_APPROVAL, CONFIGURE, ACTIVE, AUTO_RESOLVE } from '@/helpers/integration-constants.js'
  import axios from 'axios'
  import IntegrationItem from '@/components/integrations/IntegrationItem'
  import BreezewayLogo from '@/assets/breezeway.png'
  import NoiseAwareLogo from '@/assets/NoiseAware_Logo_Bug_Cream_on_Green.png'
  import BeHome247Logo from '@/assets/behome.png'
  import AuthorizeIntegrationModal from '@/components/integrations/AuthorizeIntegrationModal.vue'

  export default {
    name: 'ManageIntegrations',
    components: {
      IntegrationItem,
      AuthorizeIntegrationModal,
    },
    data: () => ({
      loading: false,
      authorized: false,
      showAuthorizationModal: false,
      authorizeIntegrationCandidate: {},
      partnersList: [],
      partnerStatusList: [],
      selectedIntegration: '',
      deactivateModal: false,
      autoResolveStatus: CONNECT,
    }),
    computed: {
      ...mapGetters({
        selectedOrg: 'Permissions/currentSelectedOrg',
        currentUser: 'Users/currentUser',
        canAdminOrg: 'Permissions/canAdminOrg',
        isInternal: 'Permissions/isInternal',
      }),
      integrationWhiteList () {
        return {
          Breezeway: {
            name: 'Breezeway',
            logo: BreezewayLogo,
            alt: 'breezeway',
            configurable: true,
            description: 'Coordinate, communicate and verify property cleaning, maintenance and operations, and manage guest messaging, all in one integrated platform. Integrate NoiseAware and Breezeway to resolve noise issues easily with automatic messages to guests.',
            enableIntegration: (integration) => { this.openAuthorizationModal(integration) },
          },
          [AUTO_RESOLVE]: {
            name: AUTO_RESOLVE,
            logo: NoiseAwareLogo,
            alt: 'noiseaware',
            configurable: false,
            description: 'Our trained agents will manage your reservation data so that you can resolve noise issues easily with automatic messages sent to your guests directly from NoiseAware. Supports most major property management systems.',
            enableIntegration: (autoResolve) => { this.openAuthorizationModal(autoResolve) },
            integrationState: this.autoResolveStatus,
          },
          BeHome247: {
            name: 'BeHome247',
            logo: BeHome247Logo,
            alt: 'behome247',
            configurable: false,
            description: 'BeHome247 is a streamlined hospitality operations platform that lets you manage property access, housekeeping, vendor payments and guest communications from one easy-to-use dashboard. Connect to enable AutoResolve messages to your guests.',
            enableIntegration: () => { this.openBeHomeLink() },
          },
        }
      },
      integrationsList () {
        const partnersWithStatusList = this.partnersList
          .filter(partner => this.integrationWhiteList[partner.name])
          .map(integration => ({
            integrationState: this.partnerStatusList.find(status => status.PartnerName === integration.name)?.Status,
            ...integration,
            ...this.integrationWhiteList[integration.name],
          }))
        return [this.integrationWhiteList[AUTO_RESOLVE], ...partnersWithStatusList]
      },
      activeIntegrationsList () {
        return this.integrationsList.filter(integration => !integration.disabled && [CONFIGURE, WAITING_FOR_APPROVAL, ACTIVE].includes(integration.integrationState))
      },
      availableIntegrationsList () {
        return this.integrationsList.filter(integration => !integration.disabled && (integration.integrationState === CONNECT))
      },
      listPayload () {
        return {
          filter: [{ key: 'organization_id', operator: '=', value: this.orgId }],
        }
      },
      orgId () {
        return this.$route.params.orgId
      },
    },
    watch: {
      async selectedOrg (newOrg) {
        const newId = newOrg.id
        if (!newId || !(this.isInternal || this.canAdminOrg(newId))) this.$router.push('/')
        else {
          this.$router.push(`/organizations/${newId}/integrations`)
          await this.initializeList()
        }
      },
    },
    async created () {
      await this.initializeList()
    },
    methods: {
      async initializeList () {
        this.loading = true
        await this.fetchIntegrationsList()
        await this.fetchAutoResolve()
        this.loading = false
      },
      async fetchIntegrationsList () {
        const partnerResponse = await axios.post(`${this.$store.state.baseURL}/partner/list`, this.listPayload)
        this.partnersList = partnerResponse.data.data
        this.partnerStatusList = await Promise.all(partnerResponse.data.data.map(partner => this.fetchIntegrationStatus(partner.name)))
      },
      async fetchIntegrationStatus (partnerName) {
        const res = await axios.get(`${this.$store.state.baseURL}/partner/orgpartnerrelationstatus/${this.orgId}/${partnerName}`)
        return res.data.data
      },
      async fetchAutoResolve () {
        return axios.get(`${this.$store.state.baseURL}/user/notification/autoresolve/${this.orgId}`)
          .then((res) => { this.autoResolveStatus = res.data.data.status })
      },
      openAuthorizationModal (integrationPartner) {
        this.authorizeIntegrationCandidate = integrationPartner
        this.showAuthorizationModal = true
      },
      async handleAuthorize (integrationId) {
        if (this.authorizeIntegrationCandidate.name === AUTO_RESOLVE) this.handleAuthorizeAutoResolve()
        else this.handleAuthorizeIntegration(integrationId)
      },
      async handleAuthorizeAutoResolve () {
        await axios.post(`${this.$store.state.baseURL}/user/notification/autoresolve`, { orgID: parseInt(this.orgId), status: WAITING_FOR_APPROVAL })
        this.authorized = true
        await this.fetchAutoResolve()
      },
      async handleAuthorizeIntegration (integrationId) {
        await axios.post(`${this.$store.state.baseURL}/partner/authorize`, { partnerID: integrationId, organizationID: parseInt(this.orgId), status: WAITING_FOR_APPROVAL })
        this.authorized = true
        await this.fetchIntegrationsList()
      },
      handleCancel () {
        this.showAuthorizationModal = false
        this.authorized = false
        this.authorizeIntegrationCandidate = {}
      },
      beginDeactivationSequence ({ name, id }) {
        this.$root.$confirm({
          minHeight: 510,
          width: 400,
          title: 'Revoke Access',
          text: `${name} will no longer be able to access or edit your NoiseAware data if you deactivate your integration.`,
          cancelText: 'CANCEL',
          confirmText: 'DEACTIVATE',
          icon: '$faUnlink',
        }).then(confirm => {
          this.handleDeactivation(name, id)
        })
      },
      async handleDeactivation (name, id) {
        if (name === AUTO_RESOLVE) this.deactivateAutoResolve()
        else this.deactivateIntegration(id)
      },
      async deactivateAutoResolve () {
        await axios.delete(`${this.$store.state.baseURL}/user/notification/autoresolve/${this.orgId}`)
        await this.fetchAutoResolve()
      },
      async deactivateIntegration (id) {
        await axios.delete(`${this.$store.state.baseURL}/partner/revoke`, { data: { partnerID: id, organizationID: parseInt(this.orgId) } })
        await this.fetchIntegrationsList()
      },
      openBeHomeLink () {
        window.open('https://behome247.notion.site/Integrate-Noiseaware-with-BeHome247-fb1c04c1870a40b885ec6abf491cceec', '_blank')
      },
    },
  }
</script>
<style lang="scss">
#manage-integrations {
  padding: 0 32px;
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  .integration-list--headers {
    font-size: 25px;
    font-weight: 400;
    line-height: 33px;
    letter-spacing: 0.5px;
    margin-top: 23px;
  }
  .integration-list--rule {
    background-color: #929292;
    border: none;
    height: 1px;
    margin: 9px 0 27px 0;
  }
  .section-container {
    width: 100%;
  }
  .section-header {
    background-color: $accent;
    border-radius: 5px;
    justify-content: space-between;
    align-items: center;
    min-height: 64px;
    display: flex;
    font-size: 0.9375rem;
    line-height: 1;
    padding: 16px 24px;
    width: 100%;
    & > * {
      flex: none;
    }
  }
  .section-header--text {
    font-size:25px;
    color: white;
    font-weight: 400;
    padding-left:9px;
    vertical-align: middle;
  }
  .section-body {
    padding: 0 24px 16px;
  }
  .section-hint {
    text-decoration: none;
    vertical-align:middle;
    text-transform:uppercase;
    color:white;
    flex: none;
    margin-left: 9px;
    font-size: 13px;
    font-weight: 400;
    line-height: 16px;
    letter-spacing: 1px;
    text-align: left;
  }
  .active-integration--empty {
    text-align: center;
    & .active-integration--empty-header {
      color: $na-medium-grey;
      font-size: 25px;
      font-weight: 400;
      margin-bottom: 21px;
      margin-top: 10px;
    }
    & .active-integration--empty-icon {
      display: flex;
      background-color: $secondary;
      border-radius: 100px;
      text-align: center;
      width: 58px;
      height: 58px;
      padding: 15px;
      margin: 9px auto 2px auto;
      color: $muted;
      font-weight: 400;
    }
  }
  .integration-quiz-link {
    font-size: 13px;
    font-weight: 400;
    line-height: 16px;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: $nav-highlight;
    padding: 0;
    min-width: 0;
    border-bottom: 1px solid $nav-highlight;
  }
}
</style>
